import useEstimateCreation from "features/subscriptions/datahooks/useEstimateCreation";
import useEstimateUpdate from "features/subscriptions/datahooks/useEstimateUpdate";
import { parseEstimate } from "features/subscriptions/utils";

import type { Pricing } from "common/types";

export default function useUpdateOrCreateEstimate({
  pricing,
  quantity,
  subscriptionId,
  isCustomerSubscription,
}: {
  pricing: { plan: Pricing; addon: Pricing } | undefined;
  quantity: number;
  subscriptionId: string | undefined;
  isCustomerSubscription: boolean;
}) {
  const isCreatingNew = !subscriptionId;

  const { estimateUpdate, isLoadingEstimateUpdate, estimateUpdateError } =
    useEstimateUpdate({
      pricing,
      quantity,
      isProrated: false,
      subscriptionId: !isCustomerSubscription && subscriptionId,
      isEnabled: !isCreatingNew,
    });

  const {
    estimateUpdate: proRatedEstimateUpdate,
    isLoadingEstimateUpdate: isLoadingProRatedEstimateUpdate,
    estimateUpdateError: proRatedEstimateUpdateError,
  } = useEstimateUpdate({
    pricing,
    quantity,
    isProrated: true,
    subscriptionId: !isCustomerSubscription && subscriptionId,
    isEnabled: !isCreatingNew,
  });

  const { estimateCreation, isLoadingEstimateCreation, estimateCreationError } =
    useEstimateCreation({
      isCustomerSubscription,
      pricing,
      quantity,
      isEnabled: isCreatingNew,
    });

  const data = { price: 0, toPayNowAmount: 0 };

  if (isCreatingNew && estimateCreation) {
    const { amountDue } = parseEstimate(estimateCreation);
    data.price = amountDue;
    data.toPayNowAmount = amountDue;
  } else {
    if (estimateUpdate) {
      data.price = parseEstimate(estimateUpdate).amountDue;
    }
    if (proRatedEstimateUpdate) {
      data.toPayNowAmount = parseEstimate(proRatedEstimateUpdate).amountDue;
    }
  }

  const isLoadingEstimate = isCreatingNew
    ? isLoadingEstimateCreation
    : isLoadingEstimateUpdate || isLoadingProRatedEstimateUpdate;

  const estimateError = isCreatingNew
    ? estimateCreationError
    : estimateUpdateError || proRatedEstimateUpdateError;

  return { ...data, isLoadingEstimate, estimateError };
}
